import { useState, useEffect } from 'react';
import BookingAPI, { BookingDetailsDto, BookingOfferDto, PendingBookingDto } from '../api/BookingAPI';
import { datadogRum } from '@datadog/browser-rum';

interface UseBookingDataResult {
  bookingData: BookingOfferDto | null;
  submitBooking: (id: string, data: PendingBookingDto, locale: string) => Promise<BookingDetailsDto | null>;
  loading: boolean;
  submitting: boolean;
  error: string | null;
}

const useBookingData = (bookingWidgetId: string | undefined): UseBookingDataResult => {
  const [bookingData, setBookingData] = useState<BookingOfferDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bookingWidgetId) {
      setError('Invalid booking widget ID');
      setLoading(false);
      return;
    }

    const fetchBookingData = async () => {
      try {
        const offerResponse = await BookingAPI.get(bookingWidgetId);
        setBookingData(offerResponse.data);
      } catch (err: any) {
        console.error(err);
        setError('Failed to load booking data.');
      } finally {
        setLoading(false);
      }
    };

    fetchBookingData();
  }, [bookingWidgetId]);

  const submitBooking = async (
    bookingWidgetId: string,
    bookingData: PendingBookingDto,
    locale: string
  ) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await BookingAPI.submit(bookingWidgetId, bookingData, locale);
      
      datadogRum.addAction('Booking Submission Response', { 
        success: response.data.success,
        reason: response.data.reason || 'none'
      });
      
      return response.data;
    } catch (error) {
      console.error("Error submitting booking:", error);
      datadogRum.addAction('Booking Submission Network Error');
      throw error;
    } finally {
      setSubmitting(false);
    }
  }; 

  return { bookingData, loading, error, submitBooking, submitting };
};

export default useBookingData;